import type {
  PartnerUser,
  PartnerUserOrder,
} from '@/entities/user/api/user.api';
import { useUserList } from '@/entities/user/hooks/useUsers';
import {
  DEFAULT_VIEW_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from '@/shared/constants/pagination';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { formatCount, orDash } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';
import {
  Card,
  LabeledValue,
  ListState,
  ListToolbar,
  PageHeader,
  PageSizeSelect,
  Pagination,
  SearchInput,
  Select,
  TOOLBAR_SELECT_CLASS,
} from '@/shared/ui';
import { getRouteApi, Link } from '@tanstack/react-router';

const routeApi = getRouteApi('/partner/users');

/** Figma sort — 값은 ruda-api #2209 의 PartnerUserSort */
const ORDER_OPTIONS: { value: PartnerUserOrder; label: string }[] = [
  { value: 'COMPANY_NAME', label: '업체명순' },
  { value: 'REGISTRATION_COUNT_DESC', label: '처방 등록 많은순' },
  { value: 'REGISTRATION_COUNT_ASC', label: '처방 등록 적은순' },
  { value: 'TRADING_CLINIC_COUNT_DESC', label: '거래 병의원 많은순' },
];

const isUserOrder = (value: string | undefined): value is PartnerUserOrder =>
  ORDER_OPTIONS.some((option) => option.value === value);

/**
 * 05 업체 — 거래처 정보의 상위업체 = 로그인 업체인 사용자(업체) 목록. 조회 전용.
 * 최근 3개월 등록 처방 장수는 응답의 월(registrationYearMonth)을 그대로 쓰고, 기준 월(이번 달)을 강조한다.
 */
export function UsersPage() {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const page = search.page ?? 1;
  const pageSize = search.size ?? DEFAULT_VIEW_PAGE_SIZE;

  const { data, isLoading, isFetching, error } = useUserList({
    keyword: search.keyword,
    order: search.order,
    page: page - 1,
    size: pageSize,
  });

  // 서버 monthlyRegistrations와 같은 3개월(오래된 월부터)

  // 필터가 바뀌면 1페이지로
  const updateSearch = (patch: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch, page: undefined }) });

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <PageHeader
        title="업체"
        description="루다링크 앱에서 우리 회사와 연결된 업체예요."
      />

      <div className="flex w-full flex-col gap-4">
        <ListToolbar
          // 이전 결과를 보여 주며 새 조건으로 다시 불러오는 중
          isRefreshing={isFetching && !isLoading}
          summary={
            <span className="text-body-16 font-medium whitespace-nowrap text-fg-secondary">
              총 {formatCount(data?.totalCount ?? 0, '개 업체')}
            </span>
          }
          controls={
            <>
              <Select
                label="정렬"
                allLabel={null}
                value={search.order ?? 'COMPANY_NAME'}
                options={ORDER_OPTIONS}
                onChange={(order) =>
                  updateSearch({
                    // 기본값은 URL에 남기지 않는다
                    order:
                      isUserOrder(order) && order !== 'COMPANY_NAME'
                        ? order
                        : undefined,
                  })
                }
                className={TOOLBAR_SELECT_CLASS}
              />
              <PageSizeSelect
                value={pageSize}
                options={PAGE_SIZE_OPTIONS}
                onChange={(size) =>
                  // 기본값은 URL에 남기지 않는다. 개수가 바뀌면 1페이지로
                  updateSearch({
                    size: size === DEFAULT_VIEW_PAGE_SIZE ? undefined : size,
                  })
                }
              />
            </>
          }
          search={
            <SearchInput
              value={search.keyword}
              placeholder="업체명, 대표자, 이메일"
              onSearch={(keyword) => updateSearch({ keyword })}
            />
          }
        />

        <ListState
          isLoading={isLoading}
          error={error}
          isEmpty={data?.content.length === 0}
        />
        <ul className="flex w-full flex-col gap-2">
          {data?.content.map((user) => (
            <li key={user.userId}>
              <UserCard user={user} />
            </li>
          ))}
        </ul>
      </div>

      {data && data.totalCount > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={data.totalCount}
          onChange={(nextPage) =>
            navigate({ search: (prev) => ({ ...prev, page: nextPage }) })
          }
        />
      )}
    </div>
  );
}

interface UserCardProps {
  user: PartnerUser;
}

/** Figma card/사용자 — 왼쪽 업체·연락처, 오른쪽 거래 병의원 + 월별 처방 */
function UserCard({ user }: UserCardProps) {
  // 월 라벨은 응답의 registrationYearMonth 그대로 — 오래된 월부터, 마지막이 기준 월(이번 달)
  const monthlyRegistrations = [...user.monthlyRegistrations].sort((a, b) =>
    a.registrationYearMonth.localeCompare(b.registrationYearMonth),
  );
  const baseMonth =
    monthlyRegistrations[monthlyRegistrations.length - 1]
      ?.registrationYearMonth;

  return (
    <Card className="flex-col gap-3 xl:flex-row xl:gap-0">
      <div className="flex w-full min-w-0 flex-col gap-2 xl:flex-1">
        <p className="truncate text-subtitle-18 font-bold text-fg-primary">
          {orDash(user.companyName)}
        </p>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-body-14 font-medium text-fg-secondary">
          <span className="w-16 shrink-0 truncate">
            {orDash(user.representativeName)}
          </span>
          <span className="w-[120px] shrink-0">{orDash(user.phoneNumber)}</span>
          {/* 좁으면 이메일은 다음 줄로 */}
          <span className="max-w-full min-w-0 truncate sm:flex-1">
            {orDash(user.email)}
          </span>
        </div>
      </div>

      <div className="grid w-full grid-cols-2 gap-x-4 gap-y-2 self-stretch sm:flex sm:items-center xl:flex-1">
        <LabeledValue label="거래 병의원">
          {/* 거래 병의원 → 06 거래처 정보(담당자 필터, 거래 중) */}
          <Link
            to={ROUTE_PATHS.CLIENTS}
            // 05의 '거래 병의원 수'는 거래 중 기준 — 06 기본값이 전체라 TRADING 을 명시한다
            search={{ userIds: [user.userId], tradeStatus: 'TRADING' }}
            className="text-fg-accent hover:underline"
          >
            {formatCount(user.tradingClinicCount)}
          </Link>
        </LabeledValue>
        {monthlyRegistrations.map(
          ({ registrationYearMonth: month, imageCount }) => (
            <LabeledValue
              key={month}
              label={`${Number(month.slice(4))}월 등록 처방`}
            >
              {imageCount == null ? (
                // 그 달 거래 병의원이 없음
                <span className="text-fg-light">-</span>
              ) : (
                // 월별 장수 → 07 처방 등록 현황(해당 월 + 담당자 필터)
                <Link
                  to={ROUTE_PATHS.PRESCRIPTIONS}
                  search={{ month, userIds: [user.userId] }}
                  className={cn(
                    'hover:underline',
                    month === baseMonth && 'text-fg-success',
                  )}
                >
                  {formatCount(imageCount, '장')}
                </Link>
              )}
            </LabeledValue>
          ),
        )}
      </div>
    </Card>
  );
}
